import { useSyncStatus } from '../sync';

const STATUS_COPY: Record<
  ReturnType<typeof useSyncStatus>,
  { label: string; dot: string; text: string }
> = {
  synced: { label: 'Synced', dot: 'bg-sync-ok', text: 'text-sync-ok' },
  syncing: { label: 'Syncing…', dot: 'bg-sync-busy animate-pulse', text: 'text-sync-busy' },
  offline: { label: 'Offline', dot: 'bg-sync-off', text: 'text-fg-3' },
};

/**
 * Always-visible sync indicator (DESIGN-BRIEF §3): calm, never an error state. Reads WS-1's
 * real connection-level status hook (`src/sync/hooks.ts`); the placeholder stub that stood
 * in for it during parallel development is gone.
 *
 * `data-testid="sync-status"` + `data-status` are the WS-8 torture suite's handles, and
 * this is the *only* badge on the page — the chrome renders it once, for every route.
 */
export function SyncStatusIndicator(): JSX.Element {
  const status = useSyncStatus();
  const copy = STATUS_COPY[status];

  return (
    <span
      data-testid="sync-status"
      data-status={status}
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-xs font-medium ${copy.text}`}
      title={
        status === 'offline'
          ? 'Changes are saved on this device and will sync when you are back online.'
          : undefined
      }
    >
      <span className={`h-1.5 w-1.5 rounded-full ${copy.dot}`} aria-hidden="true" />
      {copy.label}
    </span>
  );
}
